import { Injectable } from '@angular/core';
import { TreeItem, TreeLevel, HRoute, HComponent, HElement, HSubComponent, HSubComponentElement } from '../tree.model';

@Injectable({ providedIn: 'root' })
export class ContentSerializerService {

  buildPayload(metadata: any, items: TreeItem[]): { metadata: { properties: any }, sections: HRoute[] } {
    return {
      metadata: { properties: JSON.parse(JSON.stringify(metadata || {})) },
      sections: this.serializeRoutes(items)
    };
  }

  serializeRoutes(routes: TreeItem[]): HRoute[] {
    return (routes || [])
      .filter(route => !route.isDeleted)
      .map((route, index) => this.serializeRoute(route, index));
  }

  private serializeRoute(route: TreeItem, position: number): HRoute {
    const routeModel = JSON.parse(JSON.stringify((route.item || {}) as any));
    const properties = this.getResolvedProperties(route);

    return {
      ...routeModel,
      routeId: route.id,
      id: route.id,
      route: routeModel.route ?? route.tag,
      position,
      properties,
      components: (route.children || [])
        .filter(child => !child.isDeleted && child.level === TreeLevel.Component)
        .map((component, index) => this.serializeComponent(component, index))
    } as HRoute;
  }

  private serializeComponent(component: TreeItem, position: number): HComponent {
    const componentModel = JSON.parse(JSON.stringify((component.item || {}) as any));
    const properties = this.getResolvedProperties(component);
    const componentChildren = (component.children || []).filter(child => !child.isDeleted);
    const subComponents = componentChildren
      .filter(child => child.level === TreeLevel.SubComponent)
      .map((subComponent, index) => this.serializeSubComponent(subComponent, component.id, index));

    const serialized: any = {
      ...componentModel,
      componentId: component.id,
      id: component.id,
      type: componentModel.type ?? component.tag,
      position,
      properties,
      elements: componentChildren
        .filter(child => child.level === TreeLevel.Element)
        .map((element, index) => this.serializeElement(element, index))
    };

    if (subComponents.length > 0) {
      serialized.subComponents = subComponents;
    } else {
      delete serialized.subComponents;
    }

    return serialized as HComponent;
  }

  // Serializes a single page (sub-component) with its live edited values,
  // e.g. for measuring how the page would render on the /expand route.
  serializePage(subComponent: TreeItem, parentComponentId: string, position: number): HSubComponent {
    return this.serializeSubComponent(subComponent, parentComponentId, position);
  }

  private serializeSubComponent(subComponent: TreeItem, parentComponentId: string, position: number): HSubComponent {
    const model = JSON.parse(JSON.stringify((subComponent.item || {}) as any));
    const properties = this.getResolvedProperties(subComponent);

    return {
      ...model,
      componentId: subComponent.id,
      id: subComponent.id,
      parentComponentId,
      type: model.type ?? subComponent.tag,
      position,
      properties,
      elements: (subComponent.children || [])
        .filter(child => !child.isDeleted && child.level === TreeLevel.SubComponentElement)
        .map((element, index) => this.serializeSubComponentElement(element, index))
    } as HSubComponent;
  }

  private serializeElement(element: TreeItem, position: number): HElement {
    const model = JSON.parse(JSON.stringify((element.item || {}) as any));
    return {
      ...model,
      elementId: element.id,
      id: element.id,
      type: model.type ?? element.tag,
      value: this.getResolvedElementValue(element),
      position,
      properties: this.getResolvedProperties(element)
    } as HElement;
  }

  private serializeSubComponentElement(element: TreeItem, position: number): HSubComponentElement {
    const model = JSON.parse(JSON.stringify((element.item || {}) as any));
    return {
      ...model,
      elementId: element.id,
      id: element.id,
      type: model.type ?? element.tag,
      value: this.getResolvedElementValue(element),
      position,
      properties: this.getResolvedProperties(element)
    } as HSubComponentElement;
  }

  private getResolvedProperties(item: TreeItem): Record<string, any> {
    return JSON.parse(JSON.stringify(item.updatedProperties ?? (item.item as any)?.properties ?? {}));
  }

  private getResolvedElementValue(item: TreeItem): string {
    return item.updatedText ?? item.name ?? (item.item as any)?.value ?? '';
  }
}
